import { useTranslation } from "react-i18next";
import defaultContent from "../../../../content/downlineProfile";
import { RootState, useAppSelector } from "../../../../store";
import {
  useGetBronzeOverviewQuery,
  useGetPathToBronzeOverviewQuery,
} from "../../../../services/BronzeIncentives/bronzeIncentivesAPI";
import { getCurrentPeriod } from "../../../../common/util/period";
import { storedLocale } from "../../../Authentication/util";
import { useGetSsiOverviewQuery } from "../../../../services/StrongStart/ssiAPI";
import { AWARD_CODES } from "../../../../common/enums/award-codes";
import { transformCorePlusIncentive } from "../Transforms/transformKeyInformation";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import KeyInformationBlock from "../KeyInformation";
export type CorePlusIncentiveLabelKey = keyof typeof defaultContent.keyInformation.corePlusIncentiveLabels;
interface KeyInformationCPISRowProps {
  label?: CorePlusIncentiveLabelKey;
  graduated?: boolean;
  notStarted?: boolean;
}
const KeyInformationCPISRow = ({ label, graduated, notStarted }: KeyInformationCPISRowProps) => {
  const { t } = useTranslation(["downlineProfile"]);
  return (
    <>
      <div className="cpis-row">
        <span>{label && t(label, defaultContent.keyInformation.corePlusIncentiveLabels[label])}</span>
        {graduated && !notStarted ? (
          <span>{t("graduated", defaultContent.keyInformation.corePlusIncentiveLabels.graduated)}</span>
        ) : !graduated && !notStarted ? (
          <span>{t("started", defaultContent.keyInformation.corePlusIncentiveLabels.started)}</span>
        ) : (
          <span>{t("notStarted", defaultContent.keyInformation.corePlusIncentiveLabels.notStarted)}</span>
        )}
      </div>
    </>
  );
};

const KeyInformationCPISWrapper = () => {
  const { t } = useTranslation(["downlineProfile"]);
  const affAbo = useAppSelector((state: RootState) => state.profile.downlineProfileAffAbo);

  const [aff = "", abo = ""] = affAbo.split("-");
  const { isoCountryCode = "" } = useAppSelector((state: RootState) => state.boot.user) ?? {};
  const { isoCurrencyCode = "" } = useAppSelector((state: RootState) => state.boot.shellConfig) ?? {};
  const { newPaymentScheduleYear } = useAppSelector(
    (state: RootState) => state.boot.configuration.strongStartIncentive,
  );
  const { data: ptbData, isFetching: ptbFetch } = useGetPathToBronzeOverviewQuery({
    affAbo,
    period: getCurrentPeriod(),
    locale: storedLocale(),
    countryCode: aff,
  });
  const { data: bfiData, isFetching: bfiFetch } = useGetBronzeOverviewQuery({
    affAbo,
    period: getCurrentPeriod(),
    awardCode: AWARD_CODES.BRONZE_FOUNDATION,
    locale: storedLocale(),
    isoCountryCode,
    isoCurrencyCode,
    isStandarRetailEnhancementCountry: false,
  });
  const { data: bbiData, isFetching: bbiFetch } = useGetBronzeOverviewQuery({
    affAbo,
    period: getCurrentPeriod(),
    awardCode: AWARD_CODES.BRONZE_BUILDER,
    locale: storedLocale(),
    isoCountryCode,
    isoCurrencyCode,
    isStandarRetailEnhancementCountry: false,
  });
  const { data: ssiData, isFetching: ssiFetch } = useGetSsiOverviewQuery({
    abo,
    aff,
    locale: storedLocale(),
    period: getCurrentPeriod(),
    isoCountryCode,
    isoCurrencyCode,
    newPaymentScheduleYear: `${newPaymentScheduleYear}`,
  });

  const allFetching = ptbFetch || bfiFetch || bbiFetch || ssiFetch;

  const transformedCpis = transformCorePlusIncentive(ptbData, bfiData, bbiData, ssiData);

  return (
    <>
      <KeyInformationBlock
        header={t("keyInformation.additionalInformation", defaultContent.keyInformation.corePlusIncentive)}
        isLoading={allFetching}
      >
        {allFetching ? (
          <>
            {[...Array(4)].map((_, i) => {
              return <LeafRectangleLoader key={i} isLoading={allFetching} height="1.5rem" />;
            })}
          </>
        ) : (
          <>
            {transformedCpis.map((v, i) => (
              <KeyInformationCPISRow key={i} {...v} />
            ))}
          </>
        )}
      </KeyInformationBlock>
    </>
  );
};

export default KeyInformationCPISWrapper;
